import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { format, parse } from 'date-fns';
import DateRangePicker from '@/components/ui/DateRangePicker';
import { userService } from '../../_services';
import { safeParseDate } from '@/lib/utils';
import PagesWrapper from '@/components/ui/shared/PagesWrapper';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, X, Loader2 } from 'lucide-react';
import BarListChart from '../BarListChart';
import MultiAxisBarChart from '../MultiAxisBarChart';
import ColumnPieChart from '../ColumnPieChart';
import ColorJson from '../../../src/whitelabel.json';
import { Redirect } from "react-router-dom";

function Analytics({ AnalyticData }) {
    // Get default dates
    const getDefaultDates = useCallback(() => {
        const defaultStartDate = new Date();
        defaultStartDate.setDate(defaultStartDate.getDate() - 30);
        const defaultEndDate = new Date();
        
        const storedStartDate = localStorage.getItem('User_Start_Date');
        const storedEndDate = localStorage.getItem('User_End_Date');
        
        const startdate = (storedStartDate && storedStartDate !== "" && storedStartDate !== "null")
            ? safeParseDate(storedStartDate, defaultStartDate)
            : defaultStartDate;
        const enddate = (storedEndDate && storedEndDate !== "" && storedEndDate !== "null")
            ? safeParseDate(storedEndDate, defaultEndDate)
            : defaultEndDate;
            
        return {
            startDate: format(startdate, "MM/dd/yyyy"),
            endDate: format(enddate, "MM/dd/yyyy")
        };
    }, []);

    const [userdata] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    });
    
    const defaultDates = useMemo(() => getDefaultDates(), [getDefaultDates]);
    const [startDate, setStartDate] = useState(defaultDates.startDate);
    const [endDate, setEndDate] = useState(defaultDates.endDate);
    const [graphData, setGraphData] = useState([]);
    const [searchDisplay, setSearchDisplay] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [filterTags, setFilterTags] = useState(null);
    const searchInputRef = useRef(null);
    const [showSearchResults, setShowSearchResults] = useState(false);

    // Get primary and secondary colors based on domain
    const { primaryColor, secondaryColor } = useMemo(() => {
        const host = window.location.host;
        if (host.includes('rxhacker.com') || host.includes('localhost') || 
            host.includes('tempfilter.23byt.es') || host.includes('reporting.nulifespanrx.com')) {
            return {
                primaryColor: ColorJson.ApplicationColor.Rxhacker_primary_color,
                secondaryColor: ColorJson.ApplicationColor.Rxhacker_secondary_color
            };
        }
        return {
            primaryColor: ColorJson.ApplicationColor.Glic_primary_color,
            secondaryColor: ColorJson.ApplicationColor.Glic_secondary_color
        };
    }, []);

    const getTagTypeFullName = useCallback((stext) => {
        const tagMap = {
            'PBM': 'PCN',
            'DistributorName': 'Distributor Name',
            'PhCity': 'Pharmacy City',
            'PrCity': 'Prescriber City',
            'PhState': 'Pharmacy State',
            'PrState': 'Prescriber State',
            'PhZip': 'Pharmacy ZIP',
            'PrZip': 'Prescriber ZIP',
            'PharmacyName': 'Pharmacy Name',
            'GroupNumber': 'Group Number',
            'WholesalerName': 'Wholesaler Name',
            'DrugName': 'Drug Name',
            'Prescriber': 'Prescriber Name'
        };
        return tagMap[stext] || stext;
    }, []);

    const changeData = useCallback((dateChanged, stdate, endate, wholesalerId = '-1') => {
        const startDateObj = dateChanged ? stdate : startDate;
        const endDateObj = dateChanged ? endate : endDate;
        
        const startDateFormatted = format(safeParseDate(startDateObj), "yyyyMMdd");
        const endDateFormatted = format(safeParseDate(endDateObj), "yyyyMMdd");
        const changedStartDate = format(parse(startDateFormatted, "yyyyMMdd", new Date()), "MM/dd/yyyy");
        const changedEndDate = format(parse(endDateFormatted, "yyyyMMdd", new Date()), "MM/dd/yyyy");
        
        setStartDate(changedStartDate);
        setEndDate(changedEndDate);
        
        const AdFilterJson = JSON.parse(localStorage.getItem('Analytic_Filter'));
        const APIBaseURL = '/api/Analytic';
        
        userService.GetWh_Analytic(wholesalerId, "").then(apidata => {
            if (apidata?.length > 0) {
                const filterTime = localStorage.getItem('Filter_Last_Update_Time');
                const rxsClaimType = localStorage.getItem('Updated_HD_Type');
                
                const Widget1 = JSON.stringify({
                    wname: String(wholesalerId),
                    start_date: startDateFormatted,
                    end_date: endDateFormatted,
                    rcount: 0,
                    chart_type: "columnList",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: rxsClaimType,
                    wholesaler_id: wholesalerId,
                    widget_link: apidata[0].widgetAPI
                });
                
                const Widget5 = JSON.stringify({
                    wname: String(userdata?.wholesaler_Id || ''),
                    start_date: startDateFormatted,
                    end_date: endDateFormatted,
                    rcount: 0,
                    chart_type: "pielist",
                    filtertime: filterTime,
                    rxsClaimType: rxsClaimType,
                    wholesaler_id: userdata?.wholesaler_Id
                });
                
                const MultiAxis_DeviceSignup = JSON.stringify({
                    wname: String(wholesalerId),
                    start_date: startDateFormatted,
                    end_date: endDateFormatted,
                    rcount: 0,
                    chart_type: "column_multiaxislist",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: rxsClaimType,
                    wholesaler_id: wholesalerId,
                    widget_link: ""
                });
                
                const Widget4 = JSON.stringify({
                    wname: String(wholesalerId),
                    start_date: startDateFormatted,
                    end_date: endDateFormatted,
                    rcount: 0,
                    chart_type: "columnList",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: rxsClaimType,
                    wholesaler_id: wholesalerId,
                    widget_link: ""
                });
                
                setGraphData([
                    {
                        url: APIBaseURL + "/date_wise_signup",
                        type: apidata[0].widgetType,
                        title: apidata[0].widgetTitle,
                        Subtitle: apidata[0].widgetSubTitle,
                        ColorCode: secondaryColor,
                        DataType: "NA",
                        XlabelString: "Date",
                        YlabelString: "Number of Signups",
                        interval: 'day',
                        id: "Alalytic_widget1",
                        RequestJson: Widget1,
                        RequestJson1: JSON.stringify(AdFilterJson)
                    },
                    {
                        url: APIBaseURL + "/gn_wise_signup",
                        type: apidata[1].widgetType,
                        title: apidata[1].widgetTitle,
                        Subtitle: apidata[1].widgetSubTitle,
                        ColorCode: secondaryColor,
                        DataType: "NA",
                        XlabelString: "Group Number",
                        YlabelString: "Number of Signups",
                        id: "Alalytic_widget2",
                        RequestJson: Widget1,
                        RequestJson1: JSON.stringify(AdFilterJson)
                    },
                    {
                        url: APIBaseURL + "/devicedatesignup",
                        type: "MultiBar_List",
                        title: "Client Access Link Signups",
                        Subtitle: "Number of signups through Client Access Links",
                        ColorCode: primaryColor,
                        DataType: "NA",
                        XlabelString: "Date",
                        YlabelString: "Number of Signups",
                        id: "Alalytic_widget3",
                        RequestJson: MultiAxis_DeviceSignup,
                        RequestJson1: JSON.stringify(AdFilterJson)
                    },
                    {
                        url: APIBaseURL + "/devicegnsignup",
                        type: "Bar_List",
                        title: "Client Access Link Signups by Group Number",
                        Subtitle: "Number of signups through Client Access Links for each group number",
                        ColorCode: secondaryColor,
                        DataType: "NA",
                        XlabelString: "Group Number",
                        YlabelString: "Number of Signups",
                        id: "Alalytic_widget4",
                        RequestJson: Widget4,
                        RequestJson1: JSON.stringify(AdFilterJson)
                    },
                    {
                        url: APIBaseURL + "/active_inactive_reps_bytransaction",
                        type: "Column_Pie",
                        title: "Active/Inactive Brokers by Transactions",
                        Subtitle: "Real-time active/inactive brokers by transactions",
                        XlabelString: "Text",
                        YlabelString: "Value",
                        ColorCode: primaryColor,
                        DataType: "PhState",
                        id: "Alalytic_widget5",
                        RequestJson: Widget5,
                        RequestJson1: JSON.stringify(AdFilterJson)
                    },
                    {
                        url: APIBaseURL + "/active_inactive_reps_byenrollment",
                        type: "Column_Pie",
                        title: "Active/Inactive Brokers by Enrollments",
                        Subtitle: "Real-time active/inactive brokers by enrollments",
                        XlabelString: "Text",
                        YlabelString: "Value",
                        ColorCode: primaryColor,
                        DataType: "PhState",
                        id: "Alalytic_widget6",
                        RequestJson: Widget5,
                        RequestJson1: JSON.stringify(AdFilterJson)
                    },
                    {
                        url: APIBaseURL + "/date_wise_signup_api",
                        type: "Bar_List",
                        title: "API Signups",
                        Subtitle: "Number of signups through API",
                        ColorCode: secondaryColor,
                        DataType: "NA",
                        XlabelString: "Date",
                        YlabelString: "Number of Signups",
                        interval: 'day',
                        id: "Alalytic_widget7",
                        RequestJson: Widget5,
                        RequestJson1: JSON.stringify(AdFilterJson)
                    },
                    {
                        url: APIBaseURL + "/gn_wise_signup_api",
                        type: "Bar_List",
                        title: "API Signups by Group Number",
                        Subtitle: "Number of signups through API for each group number",
                        ColorCode: secondaryColor,
                        DataType: "NA",
                        XlabelString: "Group Number",
                        YlabelString: "Number of Signups",
                        id: "Alalytic_widget8",
                        RequestJson: Widget5,
                        RequestJson1: JSON.stringify(AdFilterJson)
                    }
                ]);
            }
        }).catch(error => {});
    }, [startDate, endDate, userdata, primaryColor, secondaryColor]);

    const handleCalendar = useCallback((e, picker) => {
        const changedStartDate = format(safeParseDate(picker.startDate), "MM/dd/yyyy");
        const changedEndDate = format(safeParseDate(picker.endDate), "MM/dd/yyyy");
        localStorage.setItem('User_Start_Date', changedStartDate);
        localStorage.setItem('User_End_Date', changedEndDate);
        changeData(true, changedStartDate, changedEndDate, '-1');
    }, [changeData]);

    const handlechangea = useCallback((e) => {
        if (e && e.heading && e.value) {
            // Validate heading is not undefined or invalid
            if (e.heading === 'undefined' || !e.heading) {
                return;
            }
            
            try {
                let Created_Advance_Filter = JSON.parse(localStorage.getItem('Advance_Filter')) || {};
                
                if (Created_Advance_Filter[e.heading]) {
                    if (!Created_Advance_Filter[e.heading].includes(e.value)) {
                        Created_Advance_Filter[e.heading].push(e.value);
                    }
                } else {
                    Created_Advance_Filter[e.heading] = [e.value];
                }
                
                localStorage.setItem('Advance_Filter', JSON.stringify(Created_Advance_Filter));
                localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
                localStorage.setItem('Updated_AdFilter_Status', JSON.stringify(Created_Advance_Filter));
                
            } catch (error) {
                console.error('Error saving filter:', error);
            }
        }
        
        const startdate = format(safeParseDate(localStorage.getItem('User_Start_Date') || startDate), "MM/dd/yyyy");
        const enddate = format(safeParseDate(localStorage.getItem('User_End_Date') || endDate), "MM/dd/yyyy");
        localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
        changeData(true, startdate, enddate, '-1');
    }, [startDate, endDate, changeData]);

    const searchWholesalers = useCallback(async (text) => {
        if (text.length < 2) {
            setSearchDisplay([]);
            setShowSearchResults(false);
            setSearchLoading(false);
            return;
        }
        
        setSearchLoading(true);
        
        try {
            const response = await fetch('/api/Filter/SearchOnTypeText_GetWholesaler', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text })
            });
            const data = await response.json();
            setSearchDisplay(data || []);
            setShowSearchResults(true);
            setSearchLoading(false);
        } catch (error) {

            setSearchDisplay([]);
            setSearchLoading(false);
        }
    }, []);

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchValue(value);
        searchWholesalers(value);
    }, [searchWholesalers]);

    const handleSearchFocus = useCallback(() => {
        if (searchValue.length >= 2 && searchDisplay.length > 0) {
            setShowSearchResults(true);
        }
    }, [searchValue, searchDisplay]);

    const handleSearchItemClick = useCallback((item) => {
        setSearchValue('');
        setSearchDisplay([]);
        setShowSearchResults(false);
        
        // Create filter
        const nodetype = "WholesalerName";
        const nodevalue = item.whName;
        
        let Created_Advance_Filter = JSON.parse(localStorage.getItem('Analytic_Filter'));
        if (Created_Advance_Filter && typeof Created_Advance_Filter === 'object') {
            if (Created_Advance_Filter[nodetype]) {
                if (!Created_Advance_Filter[nodetype].includes(nodevalue)) {
                    Created_Advance_Filter[nodetype].push(nodevalue);
                }
            } else {
                Created_Advance_Filter[nodetype] = [nodevalue];
            }
        } else {
            Created_Advance_Filter = { [nodetype]: [nodevalue] };
        }
        
        localStorage.setItem('Analytic_Filter', JSON.stringify(Created_Advance_Filter));
        localStorage.setItem('An_Filter_Last_Update_Time', new Date().toString());
        localStorage.setItem('An_Updated_AdFilter_Status', JSON.stringify(Created_Advance_Filter));
        
        const startdate = format(safeParseDate(localStorage.getItem('User_Start_Date') || startDate), "MM/dd/yyyy");
        const enddate = format(safeParseDate(localStorage.getItem('User_End_Date') || endDate), "MM/dd/yyyy");
        changeData(true, startdate, enddate, item.wh_Id);
    }, [startDate, endDate, changeData]);

    const handleRemoveTag = useCallback((nodename, nodevalue) => {
        try {
            let ExistJson = JSON.parse(localStorage.getItem('Analytic_Filter'));
            if (ExistJson && ExistJson[nodename]) {
                const ItemIndex = ExistJson[nodename].indexOf(nodevalue);
                if (ItemIndex > -1) {
                    ExistJson[nodename].splice(ItemIndex, 1);
                    if (ExistJson[nodename].length === 0) {
                        delete ExistJson[nodename];
                    }
                }
                
                if (Object.keys(ExistJson).length === 0) {
                    localStorage.setItem('Analytic_Filter', null);
                    setFilterTags(null);
                } else {
                    localStorage.setItem('Analytic_Filter', JSON.stringify(ExistJson));
                    setFilterTags(ExistJson);
                }
                
                handlechangea();
            }
        } catch (error) {

        }
    }, [handlechangea]);

    const handleResetButton = useCallback(() => {
        localStorage.setItem('Analytic_Filter', null);
        setFilterTags(null);
        handlechangea();
    }, [handlechangea]);

    useEffect(() => {
        changeData();
        try {
            const storedFilter = JSON.parse(localStorage.getItem('Analytic_Filter'));
            setFilterTags(storedFilter);
        } catch {
            setFilterTags(null);
        }
    }, []);

    // Auth checks
    const userType = userService.Get_User_Type();
    if (userService.Check_LoggedIn_Status() === 'Login') {
        return <Redirect to="/login" />;
    }
    if (['wholesaler', 'distributor', 'subadmin'].includes(userType?.toLowerCase())) {
        return <Redirect to='/pageNotFound' />;
    }

    return (
        <PagesWrapper>
            <div className="flex flex-col gap-4">
                <div className="flex items-center">
                    <h1 className="text-2xl font-semibold text-foreground">
                        Signups
                    </h1>
                </div>

                <Card>
                    <CardContent className="analytics-card-content">
                        <div className="flex flex-col lg:flex-row gap-4 flex-wrap">
                            <DateRangePicker
                                className="w-[320px] max-w-full h-10"
                                initialSettings={{
                                    startDate: startDate,
                                    endDate: endDate,
                                    maxDate: new Date(),
                                    locale: {
                                        format: 'MM/DD/YYYY'
                                    }
                                }}
                                onApply={handleCalendar}>
                                <div className="flex items-center gap-0 w-[320px] max-w-full rounded-md overflow-hidden border border-input bg-card hover:shadow-sm transition-shadow h-10">
                                    <div className="flex items-center gap-1 px-3 flex-1">
                                        <span className="text-sm text-muted-foreground whitespace-nowrap">From</span>
                                        <Input
                                            type="text"
                                            className="bg-transparent text-sm cursor-pointer w-full !border-0 outline-none h-auto p-0 rounded-none"
                                            value={startDate}
                                            readOnly
                                        />
                                    </div>
                                    <div className="h-6 w-px bg-border flex-shrink-0" />
                                    <div className="flex items-center gap-1 px-3 flex-1">
                                        <span className="text-sm text-muted-foreground whitespace-nowrap">To</span>
                                        <Input
                                            type="text"
                                            className="bg-transparent text-sm cursor-pointer w-full !border-0 outline-none h-auto p-0 rounded-none"
                                            value={endDate}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            </DateRangePicker>
                            
                            {/* Search - flexible width, grows to fill space */}
                            <div className="relative flex-1 min-w-[200px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <Input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Search wholesaler..."
                                    className="searchFilter pl-9 w-full"
                                    value={searchValue}
                                    onChange={handleSearchChange}
                                    onFocus={handleSearchFocus}
                                    onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                                />
                                {searchLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
                                {showSearchResults && searchDisplay.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 mt-1 w-full bg-popover border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                                        {searchDisplay.map((item, index) => (
                                            <div
                                                key={index}
                                                className={`px-3 py-2 hover:bg-accent cursor-pointer text-sm ${index < searchDisplay.length - 1 ? 'border-b' : ''}`}
                                                onClick={() => handleSearchItemClick(item)}
                                            >
                                                {item.whName}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Filter Tags - Outside Card */}
                {filterTags && Object.keys(filterTags).length > 0 && (
                    <div className="flex items-start gap-4">
                        {/* Scrollable tags container */}
                        <div className="overflow-x-auto">
                            <div className="flex items-center gap-4 min-w-min">
                                {Object.keys(filterTags).map((key) =>
                                    filterTags[key].map((value, index) => (
                                        <Badge
                                            key={`${key}-${index}`}
                                            variant="outline"
                                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-normal leading-none whitespace-nowrap bg-primary/10 text-primary border-primary"
                                        >
                                            {getTagTypeFullName(key)}: {value}
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleRemoveTag(key, value)}
                                                className="h-4 w-4 p-0 hover:bg-transparent hover:text-current [&_svg]:size-2.5"
                                            >
                                                <X />
                                            </Button>
                                        </Badge>
                                    ))
                                )}
                            </div>
                        </div>
                        {/* Reset button - fixed outside scroll */}
                        <Button
                            variant="ghost" className="h-auto py-1 px-3 gap-1.5 whitespace-nowrap flex-shrink-0 leading-[1.15] bg-foreground text-card border border-foreground hover:bg-foreground hover:text-card hover:opacity-85"
                            onClick={handleResetButton}
                        >
                            Reset
                        </Button>
                    </div>
                )}

                <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
                    {graphData.map((item, index) => (
                        <div key={`graph-${item.id}-${index}`} className="min-h-[400px]">
                            {item.type === "Bar_List" && (
                                <BarListChart
                                    data={item}
                                    _Index={index}
                                    handlechangea={handlechangea}
                                />
                            )}
                            {item.type === "MultiBar_List" && (
                                <MultiAxisBarChart
                                    data={item}
                                    _Index={index}
                                    handlechangea={handlechangea}
                                />
                            )}
                            {item.type === "Column_Pie" && (
                                <ColumnPieChart
                                    data={item}
                                    _Index={index}
                                    handlechangea={handlechangea}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </PagesWrapper>
    );
}

export default Analytics;
